import { useUploadFile } from '@dealstack/frontend/shared/components/DragAndDrop/useUploadFile';
import {
	uploadDocumentFile,
	uploadFile,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';

export const useUploadDocumentFileController = (
	onUploadNewDocument: (data: ApiV1.Response<unknown>) => Promise<void>
) => {
	const uploadFileRequest = useWrappedRequest(uploadFile);
	const file = uploadFileRequest?.response?.payload;

	const [isUploading, setIsUploading] = useState<boolean>(false);

	const uploadDocumentFileRequest = useMutation({
		mutationFn: (variables: Parameters<typeof uploadDocumentFile>[0]) =>
			uploadDocumentFile(variables),

		async onSuccess(data) {
			await onUploadNewDocument(data);
		},
	});

	const { onUpload, isUploadError, isUploadSuccess } = useUploadFile({
		uploadFileRequest,
	});

	const uploadOnDrop = async (files: File[]) => {
		files.forEach((file) => onUpload(file));
		setIsUploading(true);
	};
	const onFilesDrop = (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
		if (!acceptedFiles?.length || rejectedFiles?.length) {
			return;
		}
		void uploadOnDrop(acceptedFiles);
	};

	const { open, fileRejections, getInputProps } = useDropzone({
		onDrop: onFilesDrop,
		noClick: true,
		accept: { 'application/pdf': ['.pdf'] },
		multiple: false,
	});
	const onUploadButton = () => {
		open();
	};

	useEffect(() => {
		if (isUploading && isUploadSuccess && file?.id) {
			uploadDocumentFileRequest.mutate({
				fileId: file?.id,
			});
			setIsUploading(false);
		}
	}, [isUploadSuccess, file, isUploading]);

	useEffect(() => {
		if (isUploadError) {
			setIsUploading(false);
		}
	}, [isUploadError]);

	return {
		onUploadButton,
		fileRejections,
		isUploadError,
		isUploadSuccess,
		file,
		getInputProps,
		isUploading,
		isLoading: uploadFileRequest?.isLoading,
		setIsUploading,
		documentWorkflowFileId: uploadDocumentFileRequest?.data?.payload,
	};
};
